import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Command, CommandBase } from '../../../core/model/command';
import { CommandController } from '../../../core/component/base/command-controller.base';
import { ColumnDataType, ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { ListValue, ListViewRow } from '../../../core/model/list-view-row';
import { ListColumn } from '../../../core/model/list-view-column';
import { MenuItemDefinition } from '../../../core/model/menu-item-definition';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { CommandHandlerResolver } from '../../../core/service/command/command-handler-resolver.service';
import { LeaseAgreementCategory } from '../../model/const/enums';
import { LeaseRow } from '../../model/lease-row';
import { LocalDate } from '../../../core/model/local-date';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { getTranslation } from '../../../core/helper/translation';
import { Article } from '../../model/article';
import { PropertyTaxRentalChangeModel } from '../../model/property-tax-rental-change';
import { ArticleService } from '../../service/data/article.service';
import { Observable, Subscription } from 'rxjs';
import { Contract } from '../../model/contract';
import { ContractService } from '../../service/base/contract';
import { DataService } from '../../../core/service/base/data.service';
import { SidebarService } from '../../../core/service/sidebar.service';
import { AccountPlanDimensionsService } from '../../../accounting/service/data/account-plan-dimensions.service';
import { defaultDimensionLabels } from '../../../accounting/model/dimension-labels';
import { LanguageService } from '../../../core/service/language.service';
import { Money } from '../../../core/model/money';

enum LeaseAgreementRowCommands {
  edit = 'editLeaseAgreementRow',
  delete = 'deleteLeaseAgreementRow',
}

class LeaseRowListViewRow extends ListViewRow<LeaseRow> {
  override getValue(column: ListColumn<LeaseRow>): any {
    if (!this.dataSource.voided || !this.isAmountColumn(column)) {
      return super.getValue(column);
    }

    const zeroAmount = new Money(this.getCurrencyCode(column), 0);
    return column.converter ? column.converter(zeroAmount) : zeroAmount;
  }

  override addToSummaryValue(summaries: ListValue[], column: ListColumn<LeaseRow>): void {
    if (!this.dataSource.voided || !this.isAmountColumn(column)) {
      super.addToSummaryValue(summaries, column);
      return;
    }

    const currencyCode = this.getCurrencyCode(column);
    const hasCurrencySummary = summaries.some(summary => (summary.value as Money)?.currencyCode === currencyCode);
    if (!hasCurrencySummary) {
      summaries.push({ displayValue: undefined, value: new Money(currencyCode, 0) });
    }
  }

  private isAmountColumn(column: ListColumn<LeaseRow>): boolean {
    return column.dataType === ColumnDataType.money || column.dataType === ColumnDataType.moneyOptional;
  }

  private getCurrencyCode(column: ListColumn<LeaseRow>): string {
    const value = column.binding.split('.').reduce((source, property) => source?.[property], this.dataSource as any);
    return value?.currencyCode ?? value?.currency?.isoCode ?? this.dataSource.yearRent?.currencyCode ?? this.dataSource.currencyCode;
  }
}

@Component({
    selector: 'app-lease-rows',
    templateUrl: './lease-rows.component.html',
    styleUrls: ['./lease-rows.component.scss'],
    providers: [CommandDispatcher],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LeaseRowsComponent extends CommandController implements OnChanges, OnInit {
  @ViewChild('leaseAgreementSidebarContent', { static: false }) leaseAgreementSidebarContent: TemplateRef<any>;

  

  constructor(
    handlerResolver: CommandHandlerResolver,
    fb: FormBuilder,
    private readonly _factory: ColumnDefinitionFactory,
    private readonly _languageService: LanguageService,
    private readonly _dimensionService: AccountPlanDimensionsService,
    fcf: FormControlExFactory,
    commandDispatcher: CommandDispatcher,
    private readonly _articleService: ArticleService,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _sidebarService: SidebarService
  ) {
    super(commandDispatcher, handlerResolver);

    this.columnDefinition = [
      this._factory.create('LEASE_AGREEMENT_ROW.ARTICLE_CODE', 'article.code'),
      this._factory.create('LEASE_AGREEMENT_ROW.INVOICE_TEXT', 'invoiceText'),
      this._factory.create('INVOICE_ROW_GROUP.INVOICE_ROW_GROUP', 'invoiceRowGroupCaption', ColumnVisibility.optional),
      this._factory.createDate('LEASE_AGREEMENT_ROW.FROM_DATE', 'fromDate'),
      this._factory.createDate('LEASE_AGREEMENT_ROW.TO_DATE', 'toDate'),
      this._factory.createMoney('LEASE_AGREEMENT_ROW.RENT', 'rentThisPeriod', {
        visibility: ColumnVisibility.default,
        helpText: 'LEASE_AGREEMENT_ROW.HELP_TEXT.PERIOD_RENT',
      }),
      this._factory.createMoney('LEASE_AGREEMENT_ROW.INCL_VAT_PER_PERIOD', 'rentThisPeriodInclVat', {
        visibility: ColumnVisibility.optional,
        helpText: 'LEASE_AGREEMENT_ROW.HELP_TEXT.PERIOD_RENT',
      }),
      this._factory.createMoney('LEASE_AGREEMENT_ROW.YEAR_RENT_LABEL', 'yearRentInCurrentYear', {
        visibility: ColumnVisibility.default,
        helpText: 'LEASE_AGREEMENT_ROW.HELP_TEXT.YEAR_AMOUNT_IN_CURRENT_YEAR',
      }),
      this._factory.createMoney('LEASE_AGREEMENT_ROW.YEAR_RENT', 'yearRent', {
        visibility: ColumnVisibility.default,
        helpText: 'LEASE_AGREEMENT_ROW.HELP_TEXT.YEAR_RENT',
      }),
      this._factory.createMoney('LEASE_AGREEMENT_ROW.GROSS_PER_YEAR', 'yearRentInclVat', ColumnVisibility.optional),
      this._factory.createMoney('LEASE_AGREEMENT.PERIOD_AMOUNT', 'periodRent', ColumnVisibility.optional),
      this._factory.createMoney('LEASE_AGREEMENT.PERIOD_AMOUNT_INCL_VAT', 'periodRentInclVat', ColumnVisibility.optional),
      this._factory.createMoney('COMMON.YEAR_AMOUNT_PER_SQM', 'yearRentPerSquareMeter', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT_ROW.BASE_INDEX_YEAR', 'baseIndexYear', ColumnVisibility.optional),
      this._factory.createMoney('LEASE_AGREEMENT_ROW.BASE_RENT', 'baseYearRent', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT_ROW.INDEX_SHARE', 'indexShare', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT_ROW.BASE_INDEX_VALUE', 'baseIndexValue', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT_ROW.INDEX_YEAR', 'indexYear', ColumnVisibility.optional),
      this._factory.create('LEASE_AGREEMENT_ROW.INDEX_VALUE', 'indexValue', ColumnVisibility.optional),
      this._factory.createTranslation('LEASE_AGREEMENT_ROW.INDEX_CLAUSE', 'indexClauseText', ColumnVisibility.optional),
      this._factory.create(
        'LEASE_AGREEMENT_ROW.CHARGED_MONTHS',
        'chargedMonthsLength',
        ColumnVisibility.optional,
        undefined,
        'LEASE_AGREEMENT_ROW.HELP_TEXT.CHARGED_MONTHS_LENGTH'
      ),
      this._factory.create(
        'COMMON.NOTE',
        'note',
        ColumnVisibility.optional
      ),
      this._factory.create(
        defaultDimensionLabels.projectLabel,
        'project',
        ColumnVisibility.optional
      ),
    ];

    let colYearRentThisYear = this.columnDefinition.find(x => x.caption === 'LEASE_AGREEMENT_ROW.YEAR_RENT_LABEL');
    if (colYearRentThisYear) {
      colYearRentThisYear.caption = `${getTranslation(this._languageService.getCurrentTranslations(), 'LEASE_AGREEMENT_ROW.YEAR_RENT_LABEL')} ${LocalDate.today().year}`;
    }

    this.menuDefinition = [
      new MenuItemDefinition('EDIT_BTN', LeaseAgreementRowCommands.edit),
      new MenuItemDefinition('DELETE_BTN', LeaseAgreementRowCommands.delete),
    ];

    this.displayFormGroup = fb.group({
      displayHistory: fcf.create('COMMON.DISPLAY_HISTORY', false),
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    if (this._articleSub) {
      this._articleSub.unsubscribe();
      this._articleSub = null;
    }
  }

  private _currency: string = null;
  @Input() set currency(value: string) {
    if (this._currency !== value) {
      this.updateArticles(value, this.leaseAgreementCategory);
    }
    this._currency = value;
  }
  get currency(): string {
    return this._currency;
  }
  @Input() header: boolean;
  @Input() headerActionBelow: boolean = false;
  @Input() leaseStartDate: LocalDate;
  @Input() currentDuePeriodFrom: LocalDate;
  @Input() currentDuePeriodTo: LocalDate;
  @Input() currentPeriodText: string;
  @Input() rentalObjectSize: number;
  @Input() leaseAgreementInvoicePeriod: number;
  @Input() form: FormGroup;
  @Input() itemsSource: LeaseRow[];
  @Input() cultureName: string;
  @Input() entityId: string;
  @Input() companyId: string;
  @Input() propertyTaxRentalChange?: PropertyTaxRentalChangeModel;
  @Input() rowTrackBy?: (row: LeaseRow) => unknown;
  // Parents bind the inherited `dataService` (typed DataService<TModel>) which is always a ContractService at runtime.
  private _contractService: ContractService<Contract>;
  @Input() set contractService(value: DataService<any>) {
    this._contractService = value as unknown as ContractService<Contract>;
  }
  get contractService(): ContractService<Contract> {
    return this._contractService;
  }
  @Output() itemSourceChange = new EventEmitter<LeaseRow[]>();
  @Output() leaseRowsChange = new EventEmitter<LeaseRow[]>();

  private _leaseAgreementCategory: LeaseAgreementCategory | string = null;
  // Templates bind the string category name (e.g. 'Dwelling'); the backend endpoint accepts the name.
  @Input() set leaseAgreementCategory(value: LeaseAgreementCategory | string) {
    if (this._leaseAgreementCategory !== value) {
      this.updateArticles(this.currency, value);
    }
    this._leaseAgreementCategory = value;
  }
  get leaseAgreementCategory(): LeaseAgreementCategory | string {
    return this._leaseAgreementCategory;
  }
  private _articleSub = new Subscription();
  private updateArticles(currencyCode: string, category: LeaseAgreementCategory | string) {
    if (category != null && currencyCode != null) {
      if (this._articleSub) {
        this._articleSub.unsubscribe();
      }
      this._articleSub = this._articleService.getByCategory(category, currencyCode).subscribe(data => {
        this.articles = data;
      });
    }
  }

  readonly displayFormGroup: FormGroup;
  readonly listViewRowTrackBy = (row: ListViewRow<LeaseRow>): unknown => {
    return this.rowTrackBy ? this.rowTrackBy(row.dataSource) : row;
  };
  readonly listViewRowFactory = (row: LeaseRow): ListViewRow<LeaseRow> => new LeaseRowListViewRow(row);
  private _isNew: boolean;
  columnDefinition: ColumnDefinition[];
  menuDefinition: MenuItemDefinition[] = [];
  selectedRow: LeaseRow;
  itemsSourceAll: LeaseRow[];
  articles: Article[] = [];
  showProject: boolean;

  private _deleteItem(row: LeaseRow) {
    if (this.entityId && row.id && row.id.trim() !== '') {
      this.contractService.removeLeaseAgreementRow(this.entityId, row.id).subscribe(res => {
        this.mapResult(res);
        this.finalizeRowProcessing();
      });
    } else {
      let index = this.itemsSource.indexOf(row);
      if (index > -1) {
        this.itemsSource.splice(index, 1);
        if (this.form.pristine) this.form.markAsDirty();
      }

      let indexAll = this.itemsSourceAll.indexOf(row);
      if (indexAll > -1) {
        this.itemsSourceAll.splice(indexAll, 1);
        if (this.form.pristine) this.form.markAsDirty();
      }
      this.leaseRowsChange.emit(this.itemsSource);
      this.finalizeRowProcessing();
    }
  }

  private _editItem(row: LeaseRow) {
    this.selectedRow = this.itemsSource.find(x => x.id === row.id);
    this.openLeaseAgreementSidebar();
  }

  private _directSave(row: LeaseRow, isCreate: boolean): Observable<any> {
    return isCreate
      ? this.contractService.applyLeaseAgreementRow(this.entityId, row)
      : this.contractService.editLeaseAgreementRow(this.entityId, row);
  }

  canExecute(command: CommandBase): boolean {
    switch (command.id) {
      case LeaseAgreementRowCommands.edit:
      case LeaseAgreementRowCommands.delete:
        return true;

      default:
        return false;
    }
  }

  execute(command: CommandBase): void {
    switch (command.id) {
      case LeaseAgreementRowCommands.delete:
        this._deleteItem((<Command>command).context);
        break;

      case LeaseAgreementRowCommands.edit:
        this._editItem((<Command>command).context);
        break;
    }
  }

  createItem() {
    let row = new LeaseRow(this.currency);
    row.fromDate = LocalDate.fromDate(this.leaseStartDate);
    row.rentalObjectSize = this.rentalObjectSize;
    row.leaseAgreementInvoicePeriod = this.leaseAgreementInvoicePeriod;
    if (this.currentDuePeriodFrom === undefined) row.currentDuePeriodFrom = LocalDate.fromDate(this._getDefaultDuePeriodFrom());
    else row.currentDuePeriodFrom = this.currentDuePeriodFrom;
    if (this.currentDuePeriodTo === undefined) row.currentDuePeriodTo = LocalDate.fromDate(this._getDefaultDuePeriodTo());
    else row.currentDuePeriodTo = this.currentDuePeriodTo;
    this.itemsSource.push(row);
    this.itemsSourceAll.push(row);
    this.selectedRow = row;
    this._isNew = true;
    this.openLeaseAgreementSidebar();
    this._changeDetectorRef.detectChanges();
  }

  private _getDefaultDuePeriodFrom(): Date {
    let periodFrom = new Date();
    periodFrom.setMonth(periodFrom.getMonth());
    periodFrom.setDate(1);
    return periodFrom;
  }

  private _getDefaultDuePeriodTo(): Date {
    let pFrom = this._getDefaultDuePeriodFrom();
    return new Date(pFrom.getFullYear(), pFrom.getMonth() + 1, 0);
  }

  private _isInitialized = false;
  ngOnChanges(changes: SimpleChanges) {
    if (!this._isInitialized) {
      return;
    }

    if (changes['leaseStartDate'] && this.itemsSource) {
      let rows = this.itemsSource.filter(x => LocalDate.Equal(x.fromDate, changes['leaseStartDate'].previousValue));
      if (rows) {
        rows.forEach(x => (x.fromDate = changes['leaseStartDate'].currentValue));
        this.leaseRowsChange.emit(this.itemsSource);
      }
    }

    if (changes['itemsSource'] && this.itemsSource) {
      this.itemsSourceAll = this.itemsSource;
      this._onDisplayHistoryChanged(this.displayFormGroup.controls['displayHistory']?.value ?? false);
    }
  }

  ngOnInit() {
    this.itemsSourceAll = this.itemsSource;
    this._onDisplayHistoryChanged(false);
    this.displayFormGroup.controls['displayHistory'].valueChanges.subscribe(value => this._onDisplayHistoryChanged(value));

    let colPeriodAmount = this.columnDefinition.find(x => x.caption === 'LEASE_AGREEMENT_ROW.RENT');
    if (colPeriodAmount && this.currentPeriodText !== undefined) {
      colPeriodAmount.caption = `${getTranslation(
        this._languageService.getCurrentTranslations(),
        'LEASE_AGREEMENT_ROW.RENT'
      )} ${this.currentPeriodText}`;
    }
    let colPeriodAmountInclVat = this.columnDefinition.find(x => x.caption === 'LEASE_AGREEMENT_ROW.INCL_VAT_PER_PERIOD');
    if (colPeriodAmountInclVat && this.currentPeriodText !== undefined) {
      colPeriodAmountInclVat.caption = `${getTranslation(
        this._languageService.getCurrentTranslations(),
        'LEASE_AGREEMENT_ROW.INCL_VAT_PER_PERIOD'
      )} ${this.currentPeriodText}`;
    }
    if (this.companyId)
      this.AddSubscription(
        this._dimensionService.getCompanySettings(this.companyId).subscribe(res => {
          this.showProject = res.enableProject;
          const projectColumn = this.columnDefinition.find(column => column.binding === 'project');
          if (projectColumn) {
            projectColumn.caption = res.projectLabel;
          }
        })
      );

    this._isInitialized = true;
  }

  private _onDisplayHistoryChanged(displayHistory: boolean) {
    if (displayHistory) this.itemsSource = this.itemsSourceAll.filter(x => x.toDate === undefined || x.toDate.hasValue);
    else
      this.itemsSource = this.itemsSourceAll.filter(
        x => (x.toDate === undefined || !x.toDate.hasValue || LocalDate.GreaterThan(x.toDate, LocalDate.today())) && !x.voided
      );
    this._changeDetectorRef.detectChanges();
  }

  onCompleted(applyChanges: boolean) {
    // Cancel, close sidebar or form is pristine
    if (!applyChanges && this._isNew) {
      this._deleteItem(this.selectedRow);
      return;
    }

    // Form has changes and was submitted
    if (applyChanges) {
      // Agreement exists so its cause direct save
      if (this.entityId && this.entityId.trim() !== '') {
        // Direct update
        if (this.selectedRow.id && this.selectedRow.id.trim() !== '') {
          this._directSave(this.selectedRow, false).subscribe(res => {
            this.mapResult(res);
            this.finalizeRowProcessing();
          });
        }
        // Direct create
        else {
          this._directSave(this.selectedRow, true).subscribe(res => {
            this.mapResult(res);
            this.finalizeRowProcessing();
          });
        }
      }
      // Agreement does not exist so its cause local save
      else {
        if (this.form.pristine) {
          this.form.markAsDirty();
        }
        this.leaseRowsChange.emit(this.itemsSource);
        this.finalizeRowProcessing();
      }
    }
    // Form Edit was cancelled or closed
    else {
      this.finalizeRowProcessing();
    }
  }

  // Prepare LeaseRows that came from BE
  private mapResult(input: LeaseRow[]): LeaseRow[] {
    let result = input.map(row => Object.assign(new LeaseRow(), row));
    result = this.orderRows(this.itemsSource, result);
    this.itemSourceChange.emit(result);
    return result;
  }

  orderRows(base: LeaseRow[], newArray: LeaseRow[]): LeaseRow[] {
    const resultArray: LeaseRow[] = [];
    for (const item of base) {
      let newItem = newArray.find(x => x.id == item.id);
      if (newItem) {
        resultArray.push(newItem);
        newArray = newArray.filter(x => x != newItem);
      }
    }

    return [...resultArray, ...newArray];
  }

  private finalizeRowProcessing() {
    this._sidebarService.toggleSidebar(false);
    this.selectedRow = null;
    this._isNew = false;
  }

  onRowClicked(e: { context: LeaseRow; event: any }) {
    this._editItem(e.context);
  }

  // Lease Agreement Sidebar
  openLeaseAgreementSidebar() {
    this._sidebarService.setSidebarContent({ template: this.leaseAgreementSidebarContent });
    this._sidebarService.toggleSidebar(true);
  }

  closeLeaseAgreementSidebar() {
    this._sidebarService.toggleSidebar(false);
    this.onCompleted(false);
  }
}
